<template>
  <modal
    v-if="show"
    width="650px"
    height="750px"
    bodyOverflow="auto"
    bodyPadding="0"
    :title="form.id ? t('Edit Column') : t('New Column')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/columns-settings`"
  >
    <template #default>
      <div class="layout">
        <ui-input
          v-model="form.columnName"
          :placeholder="t('Name')"
          :label="`${t('Column Name')}*`"
          name="name"
          :error-messages="getError('name')"
        />
        <ui-input
          v-model="form.description"
          :placeholder="t('Additional Info')"
          :label="t('Description')"
          name="description"
          :error-messages="getError('description')"
        />

        <div class="add-tag">
          <div>
            <label class="label">{{`${t('Formula')}*`}}</label>
            <ui-tooltip-icon
              preset="help"
              class="mb-2 ml-6"
            >
              <span v-html="t('The value is green or blue if introduced correctly. Green means a value, blue - operation applied to this value.<br>If red, the value is not valid and formula will not be saved.<br>If orange, the value is archived. This refers to custom conversions.')"></span>
            </ui-tooltip-icon>
          </div>
          <label>{{`${t('For example')}: (${t('Impressions')} + ${t('Visits')}) / ${t('Cost')}` }}</label>
          <div class="content" >
            <div class="formula-container">
              <div class="backdrop">
                <div class="highlights">
                  <mark
                    v-for="token in form.formula"
                    :key="token.value"
                    :style="{ backgroundColor: 'var(--color-white)', color: token.color }"
                  >{{ token.text }}</mark>
                </div>
                <textarea
                  class="area"
                  v-model="form.formulaText"
                  @input="handleChange"
                  ref="formulaRef"
                  @blur="formulaBlur"
                >
                </textarea>
              </div>
            </div>
            <div class="btns-container">
              <div class="d-flex">
                <button @click="addText('(')" class="calc-btn">(</button>
                <button @click="addText(')')" class="calc-btn">)</button>
              </div>
              <div class="d-flex">
                <button @click="addText('+')" class="calc-btn">+</button>
                <button @click="addText('-')" class="calc-btn">-</button>
              </div>
              <div class="d-flex">
                <button @click="addText('*')" class="calc-btn">&times;</button>
                <button @click="addText('/')" class="calc-btn">&divide;</button>
              </div>
            </div>
          </div>
          <ui-input-details :error-messages="getError('formula')"></ui-input-details>
          <div class="mb-14 mt-10 item-group-wrap">
            <div class="group v-item-group v-item-group--multiple">
              <v-chip
                v-for="item in activeMetrics"
                :key="item.value"
                :disabled="item.disabled"
                variant="flat"
                :color="$colors.green"
                @click="clickChip(item)"
              >
                {{ item.text }}
              </v-chip>
            </div>
          </div>

        </div>

        <div class="select-container">
          <ui-select
            v-model="form.format"
            :autocomplete="false"
            :items="formatTypes"
            item-text="name"
            item-value="id"
            :label="t('Format')"
            :hide-details="2"
          />
          <ui-select
            v-model="form.decimals"
            :autocomplete="false"
            :items="decimals"
            item-text="name"
            item-value="id"
            :label="t('Decimals')"
            :hide-details="2"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="actions-container">
        <error-aggregator
          v-model="errors"
          v-model:active="errorsControlActive"
        ></error-aggregator>
        <div class="actions-btn">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          />

          <ui-button
            :text="t('Save')"
            button-style="accent"
            icon="fas fa-check"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useAddNewColumnDialog } from './useAddNewColumnDialog';

const props = defineProps<{
  data: Record<string, unknown>;
}>();

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  locale,
  $colors,
  show,
  form,
  formatTypes,
  decimals,
  activeMetrics,
  formulaRef,
  errors,
  errorsControlActive,
  getError,
  checkClose,
  handleChange,
  formulaBlur,
  addText,
  clickChip,
  submit,
} = useAddNewColumnDialog(
  { data: props.data as Parameters<typeof useAddNewColumnDialog>[0]['data'] },
  () => emit('close'),
);
</script>


<style lang="scss" scoped>
  .layout {
    overflow: auto;
    flex-direction: column;
    padding: 24px;
  }
  .actions-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;
  }

  .actions-btn {
    display: flex;
    place-content: flex-end;
  }

  .item-group-wrap {
    max-height: 174px;
    overflow-y: auto;
    padding-right: 3px;

    .group {
      width: calc(100% - 4px);
    }

    :deep(.v-chip) {
      max-width: 100%;

      .v-chip__content {
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        cursor: pointer;
      }
    }
  }

  .add-tag {
    display: flex;
    flex-direction: column;
    padding: 10px 0;

    .label {
      font-size: 13px;
      font-weight: bold;
    }

    .content {
      display: flex;
      height: 106px;
      flex: 1;
    }
  }

  .formula-container {
    display: flex;
    flex: 1;
    overflow-y: auto;
    background-color: var(--color-white);
    border-radius: 6px;
    border: 1px solid var(--color-gray20);
    position: relative;

    .backdrop {
      display: table;
      position: relative;
      width:100%;
      height:100%;
    }

    .highlights, .area {
      font-size: 13px;
      font-weight: 700;
      padding:10px;
      width:100%;
      font-family: Lato, Helvetica, sans-serif, -apple-system !important;
      letter-spacing: 1px;
      word-spacing: 1px;
      white-space: break-spaces;
      word-break: break-word;
      overflow-wrap: break-word;
      overflow-x: hidden;
      border:none;
      text-indent: 0;
      min-height: 100%;
    }

    .highlights mark,
    .highlights,
    .area {
      font-size: 13px;
      font-weight: 700;
      font-family: Lato, Helvetica, sans-serif, -apple-system !important;
      letter-spacing: 1px;
      word-spacing: 1px;
      white-space: break-spaces;
      word-break: break-word;
      overflow-wrap: break-word;
      text-indent: 0;
    }

    .area {
      display: block;
      overflow: hidden;
      z-index: 2;
      margin: 0;
      color: transparent;
      background-color: transparent;
      resize: none;
      outline: 0;

      caret-color: var(--color-black);
      position: absolute;
      top:0;
      left:0;
    }
  }

  .select-container {
    display: flex;
    gap: 15px;

    div {
      flex: 1;
    }
  }

  .btns-container {
    display: flex;
    flex-direction: column;

    .calc-btn {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: 5px;
      width: 32px;
      height: 32px;
      border-radius: 6px;
      border: solid 1px var(--color-gray40);
      background-image: linear-gradient(to top, var(--color-gray10), var(--color-white));
      font-size: 18px;
    }

    div + div {
      margin-top: 5px;
    }

  }
</style>
